import makeStyles from '@mui/styles/makeStyles';
import React, {useState} from "react";
import {
    Alert,
    AlertTitle,
    Button, Checkbox, FormControl, FormControlLabel, FormHelperText, Grid, Typography,
} from "@mui/material";
import TextInput from "../../../components/forms/TextInput";
import {Link, useHistory} from "react-router-dom";
import {firebaseAuth, getErrorMessage} from "../../../firebase";
import {useQueryClient} from "react-query";
import {useSnackbar} from "notistack";
import {post, put, VENDORS} from "../../../services/Client";
import VendorData from "../../../components/VendorComponents/VendorData";
import {useGetErrorMessages} from "../../../constants/errorMessages";
import {validateEmailPass, validatePrivacyTerms, validateVendorData} from "../../../services/ValidationFormManager";
import CircularLoading from "../../../components/CircularLoading";
import {useTranslation} from "react-i18next";
import useGlobalStyles from "../../../services/useGlobalStyles";
import clsx from "clsx";
import {ArrowBack} from "@mui/icons-material";
import {useGetProfileUser} from "../../../services/UserDataManager";
import PhonePrefixesSelect from "../../../components/VendorComponents/PhonePrefixesSelect";
import LanguageSelect from '../../../components/LanguageSelect';


const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100vh',
        padding: theme.spacing(3),
    },

    formControl: {
        width: '100%'
    },
    title: {
        width: '100%',
        textAlign: 'center',
        marginBottom: theme.spacing(3),
    }
}));

/**
 * @param uid se è presente un uid i dati dell'utente sono presi in automatico
 * @param goBack
 * @returns {JSX.Element}
 * @constructor
 */
export default function FormNewVendor({user, goBack = null}) {
    const classes = useStyles();
    const globalClasses = useGlobalStyles();
    const {t, i18n} = useTranslation();
    let history = useHistory();
    const {enqueueSnackbar} = useSnackbar();
    const errorMessages = useGetErrorMessages();

    const [data, setData] = useState({});
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const handleOnChange = (text, field) => {
        setData({...data, [field]: text});
    };

    function save() {
        return user ? convert() : create();
    }

    function convert() {
        setLoading(true);
        let newData = {...data};
        //----controllo
        let [errorsData, isValidData] = validateVendorData(newData);//controlli divisi per riutilizzarli nel vendor profile

        setErrors({errorsData});
        if (!isValidData) {
            setLoading(false);
            enqueueSnackbar(errorMessages.ERROR_FORM, {variant: "error"});
            return;
        }
        //-------------
        //cancella password prima di mandare la richiesta
        newData.newPassword = null;
        newData.repeatPassword = null;

        newData.visible = true;
        newData.uid = firebaseAuth.currentUser.uid;
        put(`${VENDORS}/${user.uid}/promote`, {body: newData})
            .then(async () => {
                //---mandare mail di conferma---
                enqueueSnackbar(errorMessages.VENDOR_REGISTERED, {variant: "success"});
                history.go(0);
            })
            .catch(() => {
                enqueueSnackbar(errorMessages.CREATION_ERROR_VENDOR, {variant: "error"});
            })
    }

    function create() {
        setLoading(true);
        let newData = {...data};

        //--------controllo-----------
        let [errorsEmailPass, isValidEmailPass] = validateEmailPass(newData);
        if (errorsEmailPass.repeatPassword) {
            alert(t('passwordNotMatch'));
        }
        let [errorsData, isValidData] = validateVendorData(newData);//controlli divisi per riutilizzarli nel vendor profile
        
        let [errorsValidTerms, isValidTerms] = validatePrivacyTerms(newData);//controlli divisi per riutilizzarli nel vendor profile

        setErrors({ ...errorsEmailPass, ...errorsData, ...errorsValidTerms });
        if (!isValidEmailPass || !isValidData || !isValidTerms) {
            setLoading(false);
            enqueueSnackbar(errorMessages.ERROR_FORM, {variant: "error"});
            return;
        }
        //----- ------------

        firebaseAuth.createUserWithEmailAndPassword(newData.email, newData.newPassword).then(() => {
            //cancella password prima di mandare la richiesta
            newData.newPassword = null;
            newData.repeatPassword = null;

            newData.visible = true;
            newData.uid = firebaseAuth.currentUser.uid;
            post(VENDORS, {body: newData})
                .then(async () => {
                    const baseUrl = window.location.origin;
                    //---mandare mail di conferma---
                    await firebaseAuth.currentUser.sendEmailVerification({url: baseUrl + '/emailValidated?isVendor=true'});
                    await firebaseAuth.signOut();
                    enqueueSnackbar(errorMessages.VENDOR_REGISTERED, {variant: "success"});
                    history.push('/confirmEmail');
                })
                .finally(async () => {
                    setLoading(false);
                })
                .catch(() => {
                    //se c'è un errore nel salvataggio cancello l'utente firebase
                    firebaseAuth.currentUser.delete().then(() => {
                        enqueueSnackbar(errorMessages.CREATION_ERROR_VENDOR, {variant: "error"});
                    })
                });
        }).catch((e) => {
            console.log(e);
            enqueueSnackbar(errorMessages.CREATION_ERROR_VENDOR + ' ' + getErrorMessage(e), {variant: "error"});
            setLoading(false);
        })

    }

    return (
        <div className={classes.root}>
            <FormControl sx={
                {
                    m: 2,
                    minWidth: 120,
                }
            } size="small">
                <LanguageSelect value={i18n.language} onChange={({target}) => i18n.changeLanguage(target.value)}
                                color='black'/>
            </FormControl>
            {
                goBack &&
                <Grid container justifyContent="space-between" className={globalClasses.marginBottom}>
                    <Button
                        onClick={(e) => {
                            goBack();
                        }}
                        className={globalClasses.backButton}
                    >
                        <ArrowBack className={globalClasses.backButtonIcon}/>
                        {t('goBack')}
                    </Button>
                </Grid>
            }
            <Grid item xs={12} className={classes.title}>
                <Typography variant={'h5'}>{t('registerAsVendor')}</Typography>
            </Grid>
            <Grid container
                direction="row"
                alignItems="flex-start"
                className={globalClasses.marginBottom}
            >
                <Grid item xs={12} classes={globalClasses.marginBottom}>
                    <Typography variant={'h6'} align={'center'}>{t('referentData')}</Typography>
                </Grid>
                <Grid item xs={12} className={globalClasses.marginBottom}/>

                {/*--- nome  --*/}
                <Grid item xs={12} md={6} className={clsx(globalClasses.marginBottom, globalClasses.paddingMdUp)}>
                    <TextInput
                        label={t('firstName')}
                        type="text"
                        color={'primary'}
                        value={user ? user.firstName : (data.firstName || '')}
                        onTextChange={(text) => handleOnChange(text, 'firstName')}
                        error={errors.firstName || ''}
                        required
                        disabled={!!user}
                    />
                </Grid>

                {/*--- cognome  --*/}
                <Grid item xs={12} md={6} className={clsx(globalClasses.marginBottom, globalClasses.paddingMdUp)}>
                    <TextInput
                        label={t('lastName')}
                        type="text"
                        color={'primary'}
                        value={user ? user.lastName : (data.lastName || '')}
                        onTextChange={(text) => handleOnChange(text, 'lastName')}
                        error={errors.lastName || ''}
                        required
                        disabled={!!user}
                    />
                </Grid>

                {/*--- email  --*/}
                <Grid item xs={12} className={clsx(globalClasses.marginBottom, globalClasses.paddingMdUp)}>
                    <TextInput
                        label={t('email')}
                        type="text"
                        color={'primary'}
                        value={user ? user.email : (data.email || '')}
                        onTextChange={(text) => handleOnChange(text, 'email')}
                        error={errors.email || ''}
                        required
                        disabled={!!user}
                    />
                </Grid>

                {
                    !user &&
                    <>
                        {/*--- password  --*/}
                        <Grid item xs={12} md={6}
                            className={clsx(globalClasses.marginBottom, globalClasses.paddingMdUp)}>
                            <TextInput
                                label={t('password')}
                                type="password"
                                color={'primary'}
                                value={data.newPassword || ''}
                                onTextChange={(text) => handleOnChange(text, 'newPassword')}
                                error={errors.newPassword || ''}
                                required
                            />
                        </Grid>
                        {/*--- ripeti password  --*/}
                        <Grid item xs={12} md={6}
                            className={clsx(globalClasses.marginBottom, globalClasses.paddingMdUp)}>
                            <TextInput
                                label={t('repeatPassword')}
                                type="password"
                                color={'primary'}
                                value={data.repeatPassword || ''}
                                onTextChange={(text) => handleOnChange(text, 'repeatPassword')}
                                error={errors.repeatPassword || ''}
                                required
                            />
                        </Grid>
                    </>
                }
                {/*--- telefono --*/}
                <Grid item xs={12} md={6} className={clsx(globalClasses.marginBottom, globalClasses.paddingMdUp)}>
                    <Grid container
                        direction="row"
                        alignItems="flex-start">
                        <Grid item xs={4}>
                            <PhonePrefixesSelect
                                label={t('areaCode')}
                                prefix={data.phonePrefix}
                                onChange={(text) => handleOnChange(text, 'phonePrefix')}
                            />
                        </Grid>
                        <Grid item xs={8}>
                            <TextInput
                                label={t('phoneNumber')}
                                type="text"
                                color={'primary'}
                                value={data.phone || ''}
                                onTextChange={(text) => handleOnChange(text, 'phone')}
                                error={errors.phone || ''}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={12} classes={globalClasses.marginBottom}>
                <Typography variant={'h6'} align={'center'}>{t('companyData')}</Typography>
            </Grid>
            <Grid item xs={12} className={globalClasses.marginBottom}/>

            <VendorData handleOnChange={handleOnChange} vendor={data} errors={errors} hidePhone={true}/>

            <Grid item xs={12} classes={globalClasses.marginBottom} style={{ padding: "0 16px" }}>
                <FormControlLabel label={
                    <div>
                        <span>{t('vendorsTermsConsent')}</span>
                        <a href="https://www.pinbike.it/termini_utilizzo_esercenti/" target="_blank">{t('vendorsTermsLink')}</a>
                        <span>.</span>
                    </div>
                    }
                    control={
                        <Checkbox defaultChecked={false}
                            onChange={(e) => data["privacyTermsAccepted"] = e.target.checked}
                        />
                    }
                />
                <FormHelperText error={errors["privacyTermsAccepted"]}>
                    {errors["privacyTermsAccepted"] || ''}</FormHelperText>
            </Grid>

            <Grid item xs={12} style={{marginBottom: "0.5rem", padding: "16px"}}>
                <Alert severity="warning">
                    <AlertTitle sx={{fontWeight: 'bold', textTransform: 'uppercase'}}>{t('warning')}</AlertTitle>
                    {t('newVendorAlert')}
                </Alert>
            </Grid>

            <Grid container
                justifyContent={'center'}
                direction={'row'}
            >
                <Grid item xs={12} md={3}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={save}
                        disabled={loading}
                    >
                        {t('register')}
                    </Button>
                    {loading && <CircularLoading/>}
                </Grid>
            </Grid>
        </div>
    );
}
